/** A choice offered by the export-settings modal. */
export type DownloadOption = "images" | "pdf" | "html5" | "psd" | "idml";

/**
 * "png" downloads the rendered image; "html5" wraps that same render in a
 * channel-uploadable HTML5 bundle ZIP (index.html + clickTag + budgeted image);
 * "psd" and "idml" build layered Photoshop and InDesign files from the same markup;
 * "pdf" collects every render in the export into one labeled review document.
 */
export type AdAssetDownloadFormat = "png" | "pdf" | "html5" | "psd" | "idml";

/** Encoding for an image file or a PDF page. PNG is lossless; JPG trades fidelity for file size. */
export type ImageFileType = { type: "png" } | { type: "jpg"; quality: number };

export const PNG_IMAGE_FILE_TYPE: ImageFileType = { type: "png" };

export const JPG_QUALITY_OPTIONS = [
  { label: "High", value: 0.9 },
  { label: "Medium", value: 0.8 },
  { label: "Low", value: 0.6 },
] as const;

/** A review PDF holds a whole folder, so it defaults to the smaller encoding. */
export const DEFAULT_PDF_IMAGE_FILE_TYPE: ImageFileType = {
  type: "jpg",
  quality: JPG_QUALITY_OPTIONS[0].value,
};

/** Every page renders in the tab and stays in memory until the PDF is built. */
export const MAX_PDF_EXPORT_ADS = 200;

/** Maps export-settings selections to the shared download hook's render formats. */
export const downloadOptionsToFormats = (
  options: DownloadOption[],
): AdAssetDownloadFormat[] => [
  ...(options.includes("images") ? (["png"] as const) : []),
  ...(options.includes("pdf") ? (["pdf"] as const) : []),
  ...(options.includes("html5") ? (["html5"] as const) : []),
  ...(options.includes("psd") ? (["psd"] as const) : []),
  ...(options.includes("idml") ? (["idml"] as const) : []),
];

export const DOWNLOAD_OPTION_META: Record<
  DownloadOption,
  { label: string; description?: string }
> = {
  images: {
    label: "Images",
    description: "Base image and any other created sizes",
  },
  pdf: {
    label: "PDF",
    description:
      "One document with every ad and size in a labeled grid, for review",
  },
  html5: {
    label: "HTML5 bundles",
    description:
      "Zipped HTML5 creatives for Google Ads and programmatic channels",
  },
  idml: {
    label: "InDesign files",
    description:
      "Layered IDMLs with editable text and embedded images. Install the ad’s fonts in InDesign to preserve text styling.",
  },
  psd: {
    label: "Photoshop files",
    description:
      "Layered PSDs with editable text and layer groups for the base ad and any created sizes",
  },
};
